import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  CheckCircle, XCircle, Info, Search, ChevronDown,
  MessageSquare, Clock, History, List, User, Calendar
} from 'lucide-react';
import ApprovalDetailsView, {
  TYPE_ICONS, TYPE_LABELS, getApprovalDisplayName
} from '../../components/ApprovalDetailsView';

const STATUS_COLORS = {
  PENDING:  { bg: '#FEF3C7', text: '#92400E', border: '#FCD34D' },
  APPROVED: { bg: '#DCFCE7', text: '#14532D', border: '#86EFAC' },
  REJECTED: { bg: '#FEE2E2', text: '#7F1D1D', border: '#FCA5A5' },
  CANCELLED:{ bg: '#F3F4F6', text: '#6B7280', border: '#D1D5DB' },
};

const TABS = [
  { key: 'PENDING',  label: 'Pending',        icon: Clock,    color: 'text-amber-600',  active: 'border-amber-500 text-amber-700 bg-amber-50' },
  { key: 'HISTORY',  label: 'Approved / Rejected', icon: History,  color: 'text-blue-600',   active: 'border-blue-500 text-blue-700 bg-blue-50' },
  { key: 'ALL',      label: 'All',             icon: List,     color: 'text-gray-500',   active: 'border-gray-500 text-gray-700 bg-gray-50' },
];

const fmt = d => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    + ' ' + new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

export default function ApprovalsPage() {
  const [activeTab, setActiveTab]               = useState('PENDING');
  const [approvals, setApprovals]               = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [counts, setCounts]                     = useState({ total: 0, by_type: {}, by_status: {} });
  const [typeFilter, setTypeFilter]             = useState('');
  const [search, setSearch]                     = useState('');
  const [expandedId, setExpandedId]             = useState(null);
  const [modal, setModal]                       = useState({ open: false, type: null, id: null });
  const [actionNote, setActionNote]             = useState('');
  const [selected, setSelected]                 = useState(new Set());
  const [actionInProgress, setActionInProgress] = useState(false);
  // Worker WhatsApp update (worker channel only — never admin numbers)
  const [notifyWorker, setNotifyWorker]         = useState(true);
  const [workerPhone, setWorkerPhone]           = useState('');
  const [saveWorkerPhone, setSaveWorkerPhone]   = useState(false);
  const [workers, setWorkers]                   = useState([]);

  useEffect(() => {
    api.get('/workers').then(r => setWorkers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const loadApprovals = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [appRes, countRes] = await Promise.all([
        api.get('/approvals', { params: {
          status: activeTab,
          type: typeFilter || undefined,
          limit: 200
        }}),
        api.get('/approvals/count'),
      ]);
      setApprovals(appRes.data.data || []);
      setCounts(countRes.data);
    } catch {
      if (!silent) toast.error('Failed to load approvals');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [activeTab, typeFilter]);

  useEffect(() => {
    loadApprovals(false);
    const iv = setInterval(() => loadApprovals(true), 60000);
    return () => clearInterval(iv);
  }, [loadApprovals]);

  // Reset selected when tab changes
  useEffect(() => { setSelected(new Set()); setExpandedId(null); }, [activeTab]);

  // Result of the worker's WhatsApp update (separate toast from the admin confirmation)
  const showWorkerNotifyResult = (n, name = 'Worker') => {
    if (!n) return;
    if (n.sent) return toast.success(`${name} notified on WhatsApp (+${n.phone})`);
    const msg = {
      queued_until_connected: `WhatsApp is reconnecting — ${name}'s message will be sent shortly`,
      no_worker_number: `No WhatsApp number for ${name} — message not sent`,
      admin_number: 'Not sent: that number is an admin number. Worker updates only go to worker numbers.',
      invalid_number: 'Not sent: the worker number is not valid',
      service_disabled: 'Not sent: WhatsApp is not running on the server',
      requester_is_admin: null,
    }[n.reason];
    if (msg === null) return;
    if (n.reason === 'queued_until_connected') toast(msg);
    else toast.error(msg || `Worker WhatsApp not sent: ${n.reason}`);
  };

  const openApprove = (approval) => {
    setActionNote('');
    setNotifyWorker(true);
    setWorkerPhone(approval.user_phone || '');
    setSaveWorkerPhone(!approval.user_phone);
    setModal({ open: true, type: 'approve', id: approval.id });
  };

  const handleApprove = async (id) => {
    setActionInProgress(true);
    try {
      const approval = approvals.find(a => a.id === id);
      const canNotify = approval && !['admin', 'superadmin'].includes(approval.user_role);
      const res = await api.post(`/approvals/${id}/approve`, {
        adminNotes: actionNote,
        notifyWorker: canNotify && notifyWorker,
        workerPhone: workerPhone.trim(),
        saveWorkerPhone,
      });
      toast.success('You have approved successfully');
      showWorkerNotifyResult(res.data.worker_notification, approval?.user_name);
      setModal({ open: false });
      setActionNote('');
      loadApprovals(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Approval failed');
    } finally {
      setActionInProgress(false);
    }
  };

  const handleReject = async (id) => {
    if (!actionNote.trim()) { toast.error('Please provide a rejection reason'); return; }
    setActionInProgress(true);
    try {
      await api.post(`/approvals/${id}/reject`, { reason: actionNote });
      toast.success('Request rejected.');
      setModal({ open: false });
      setActionNote('');
      loadApprovals(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Rejection failed');
    } finally {
      setActionInProgress(false);
    }
  };

  const handleBulkApprove = async () => {
    if (selected.size === 0) { toast.error('Select approvals first'); return; }
    if (!window.confirm(`Approve ${selected.size} requests?`)) return;
    setActionInProgress(true);
    try {
      const res = await api.post('/approvals/bulk-approve', { approval_ids: Array.from(selected), notifyWorkers: true });
      toast.success('You have approved successfully');
      const notified = (res.data.results || []).filter(r => r.worker_notification?.sent).length;
      if (notified) toast.success(`${notified} worker(s) notified on WhatsApp`);
      if (res.data.error_count) toast.error(`${res.data.error_count} request(s) could not be approved`);
      setSelected(new Set());
      loadApprovals(false);
    } catch {
      toast.error('Bulk approval failed');
    } finally {
      setActionInProgress(false);
    }
  };

  const parseData = (raw) => {
    if (!raw) return {};
    try { return typeof raw === 'string' ? JSON.parse(raw) : raw; }
    catch { return {}; }
  };

  const filtered = approvals.filter(a =>
    !search ||
    (a.user_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (a.created_by_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (a.approved_by_name || '').toLowerCase().includes(search.toLowerCase())
  );

  const pendingCount  = counts.by_status?.PENDING  || counts.total || 0;
  const approvedCount = counts.by_status?.APPROVED || 0;
  const rejectedCount = counts.by_status?.REJECTED || 0;

  return (
    <div className="space-y-5">

      {/* ── TABS ── */}
      <div className="flex border-b border-gray-200">
        {TABS.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          let badge = null;
          if (tab.key === 'PENDING')  badge = pendingCount;
          if (tab.key === 'HISTORY')  badge = approvedCount + rejectedCount;
          if (tab.key === 'ALL')      badge = (counts.by_status ? Object.values(counts.by_status).reduce((a, b) => a + b, 0) : 0);
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-all whitespace-nowrap ${
                isActive ? tab.active + ' border-current' : 'border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
              {badge > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  isActive
                    ? tab.key === 'PENDING' ? 'bg-amber-200 text-amber-800' : 'bg-blue-200 text-blue-800'
                    : 'bg-gray-100 text-gray-500'
                }`}>{badge}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── STAT STRIP (pending tab only) ── */}
      {activeTab === 'PENDING' && Object.keys(counts.by_type || {}).length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {Object.entries(counts.by_type || {}).map(([type, count]) => (
            <div key={type}
              onClick={() => setTypeFilter(typeFilter === type ? '' : type)}
              className={`p-3 rounded-xl border cursor-pointer transition-all ${
                typeFilter === type ? 'bg-amber-50 border-amber-300' : 'bg-gray-50 border-gray-100 hover:border-amber-200'
              }`}>
              <p className="text-xs text-gray-400 uppercase font-medium">{TYPE_ICONS[type]} {TYPE_LABELS[type] || type}</p>
              <p className="text-2xl font-bold text-gray-700">{count}</p>
            </div>
          ))}
        </div>
      )}

      {/* ── HISTORY STAT STRIP ── */}
      {activeTab === 'HISTORY' && (
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
            <CheckCircle className="w-8 h-8 text-green-500 flex-shrink-0" />
            <div>
              <p className="text-xs text-green-600 font-medium uppercase">Total Approved</p>
              <p className="text-3xl font-black text-green-700">{approvedCount}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
            <XCircle className="w-8 h-8 text-red-400 flex-shrink-0" />
            <div>
              <p className="text-xs text-red-500 font-medium uppercase">Total Rejected</p>
              <p className="text-3xl font-black text-red-600">{rejectedCount}</p>
            </div>
          </div>
        </div>
      )}

      {/* ── CONTROLS ── */}
      <div className="flex flex-col sm:flex-row gap-3 items-center">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by worker / approved by..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Types</option>
          <option value="PROJECT">Projects</option>
          <option value="MAIN_PO">Manufacturing Orders</option>
          <option value="CASHBOOK_ADVANCE">Cash Advances</option>
          <option value="CASHBOOK_EXPENSE">Cash Expenses</option>
          <option value="QUALITY_REPORT">Quality Reports</option>
          <option value="CFT">CFT Sheets</option>
          <option value="ITEM">Items</option>
          <option value="CHALLAN">Challans</option>
          <option value="PO_STATUS">PO Updates</option>
          <option value="SALE">Sales</option>
        </select>
        {activeTab === 'PENDING' && selected.size > 0 && (
          <button
            onClick={handleBulkApprove}
            disabled={actionInProgress}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 font-medium text-sm whitespace-nowrap"
          >
            ✅ Approve {selected.size} Selected
          </button>
        )}
      </div>

      {/* ── LIST ── */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-3" />
          <p className="text-gray-500">Loading...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-xl border border-dashed border-gray-200">
          {activeTab === 'PENDING'
            ? <><CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" /><p className="text-gray-600 font-semibold">All caught up!</p><p className="text-sm text-gray-400">No pending approvals right now</p></>
            : <><History className="w-12 h-12 text-gray-300 mx-auto mb-3" /><p className="text-gray-500 font-semibold">No history found</p></>
          }
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(approval => {
            const data       = parseData(approval.requested_data);
            const isExpanded = expandedId === approval.id;
            const sc         = STATUS_COLORS[approval.status] || STATUS_COLORS.PENDING;
            const headerName = getApprovalDisplayName(data);
            const isPending  = approval.status === 'PENDING';

            return (
              <div key={approval.id}
                className={`bg-white rounded-xl border shadow-sm hover:shadow-md transition-shadow ${
                  approval.status === 'APPROVED' ? 'border-green-200' :
                  approval.status === 'REJECTED' ? 'border-red-200' : 'border-gray-200'
                }`}>

                {/* Card Header */}
                <div className="p-4 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : approval.id)}>
                  <div className="flex items-start gap-3">

                    {/* Checkbox — only for pending */}
                    {isPending && (
                      <input
                        type="checkbox"
                        checked={selected.has(approval.id)}
                        onChange={e => {
                          const s = new Set(selected);
                          e.target.checked ? s.add(approval.id) : s.delete(approval.id);
                          setSelected(s);
                        }}
                        onClick={e => e.stopPropagation()}
                        className="w-4 h-4 accent-blue-500 mt-1 flex-shrink-0"
                      />
                    )}

                    <span className="text-2xl flex-shrink-0">{TYPE_ICONS[approval.type]}</span>

                    <div className="flex-1 min-w-0">
                      {/* Title row */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-gray-900">{TYPE_LABELS[approval.type] || approval.type}</span>
                        {headerName && <span className="text-gray-500 text-sm truncate">— {headerName}</span>}
                        <span className="px-2 py-0.5 rounded-full text-xs font-bold border flex-shrink-0"
                          style={{ background: sc.bg, color: sc.text, borderColor: sc.border }}>
                          {approval.status}
                        </span>
                        <span className="text-xs text-gray-400 font-mono">#{approval.id}</span>
                      </div>

                      {/* Meta row */}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5">
                        {/* Created by */}
                        <span className="flex items-center gap-1 text-sm text-gray-500">
                          <User className="w-3.5 h-3.5 text-gray-400" />
                          Created by:{' '}
                          <strong className="text-gray-700">{approval.created_by_name || approval.user_name || '—'}</strong>
                        </span>
                        {/* Requested at */}
                        <span className="flex items-center gap-1 text-xs text-gray-400">
                          <Calendar className="w-3 h-3" />
                          {fmt(approval.created_at)}
                        </span>
                      </div>

                      {/* Approved/Rejected row — history only */}
                      {approval.status === 'APPROVED' && (
                        <div className="flex items-center gap-2 mt-1.5 text-sm">
                          <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                          <span className="text-green-700 font-semibold">Approved by {approval.approved_by_name || 'Admin'}</span>
                          <span className="text-xs text-gray-400">{fmt(approval.approved_at)}</span>
                          {approval.admin_notes && (
                            <span className="text-xs text-gray-500 italic">"{approval.admin_notes}"</span>
                          )}
                        </div>
                      )}
                      {approval.status === 'REJECTED' && (
                        <div className="flex items-center gap-2 mt-1.5 text-sm flex-wrap">
                          <XCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                          <span className="text-red-600 font-semibold">Rejected by {approval.approved_by_name || 'Admin'}</span>
                          <span className="text-xs text-gray-400">{fmt(approval.rejected_at || approval.approved_at)}</span>
                          {approval.rejected_reason && (
                            <span className="text-xs text-gray-500 italic">— "{approval.rejected_reason}"</span>
                          )}
                        </div>
                      )}
                    </div>

                    <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform shrink-0 mt-0.5 ${isExpanded ? 'rotate-180' : ''}`} />
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t border-gray-100 p-4 bg-gray-50 space-y-4 rounded-b-xl">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <Info className="w-4 h-4 text-blue-400" />
                        Request Details
                      </h4>
                      <ApprovalDetailsView data={data} type={approval.type} />
                    </div>

                    {approval.admin_notes && approval.status !== 'APPROVED' && (
                      <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
                        <p className="text-xs font-semibold text-blue-700 mb-1 flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" /> Admin Notes
                        </p>
                        <p className="text-sm text-blue-800">{approval.admin_notes}</p>
                      </div>
                    )}

                    {/* Actions — only for PENDING */}
                    {isPending && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => openApprove(approval)}
                          className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium text-sm flex items-center justify-center gap-2"
                        >
                          <CheckCircle className="w-4 h-4" /> Approve
                        </button>
                        <button
                          onClick={() => { setActionNote(''); setModal({ open: true, type: 'reject', id: approval.id }); }}
                          className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 font-medium text-sm flex items-center justify-center gap-2"
                        >
                          <XCircle className="w-4 h-4" /> Reject
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── CONFIRM MODAL ── */}
      {modal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <h2 className="text-lg font-bold mb-1">
              {modal.type === 'approve' ? '✅ Approve Request' : '❌ Reject Request'}
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              {modal.type === 'approve'
                ? 'The resource will be created immediately after approval.'
                : 'Please provide a reason so the worker knows what to fix.'}
            </p>
            <textarea
              placeholder={modal.type === 'approve' ? 'Optional notes (e.g., "Looks good, approved")' : 'Reason for rejection (required)'}
              value={actionNote}
              onChange={e => setActionNote(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4 resize-none"
              rows={3}
              autoFocus
            />
            {modal.type === 'approve' && (() => {
              const ap = approvals.find(a => a.id === modal.id);
              if (!ap || ['admin', 'superadmin'].includes(ap.user_role)) return null;
              const saved = (ap.user_phone || '').replace(/\D/g, '');
              const typed = workerPhone.replace(/\D/g, '');
              return (
                <div className="mb-4 rounded-lg border border-green-200 bg-green-50/60 p-3 space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
                    <input type="checkbox" checked={notifyWorker} onChange={e => setNotifyWorker(e.target.checked)} />
                    Notify {ap.user_name || 'the worker'} on WhatsApp
                  </label>
                  {notifyWorker && (
                    <>
                      <input
                        list="approval-worker-phones"
                        value={workerPhone}
                        onChange={e => setWorkerPhone(e.target.value)}
                        placeholder="Worker's WhatsApp number, e.g. 98765 43210"
                        inputMode="tel"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                      <datalist id="approval-worker-phones">
                        {workers.filter(w => w.phone).map(w => <option key={w.id} value={w.phone}>{w.name}</option>)}
                      </datalist>
                      {typed && typed !== saved && (
                        <label className="flex items-center gap-2 text-xs text-gray-600">
                          <input type="checkbox" checked={saveWorkerPhone} onChange={e => setSaveWorkerPhone(e.target.checked)} />
                          Save this number to {ap.user_name || 'the worker'}'s profile
                        </label>
                      )}
                      <p className="text-xs text-gray-500">Only the worker gets this update. Admin numbers are never used here.</p>
                    </>
                  )}
                </div>
              );
            })()}
            <div className="flex gap-2">
              <button
                onClick={() => setModal({ open: false })}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => modal.type === 'approve' ? handleApprove(modal.id) : handleReject(modal.id)}
                disabled={(modal.type === 'reject' && !actionNote.trim()) || actionInProgress}
                className={`flex-1 px-4 py-2 rounded-lg text-white font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed ${
                  modal.type === 'approve' ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'
                }`}
              >
                {actionInProgress ? 'Processing...' : modal.type === 'approve' ? 'Confirm Approve' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
